import { useEffect, useState } from "react";

type Item = { id: string; label: string };

export function CaseStudyTOC({ items }: { items: Item[] }) {
  const [active, setActive] = useState(items[0]?.id);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Cache the document height and only re-measure on resize so the scroll
    // handler never forces a synchronous layout on every frame.
    let height = document.documentElement.scrollHeight - window.innerHeight;
    let ticking = false;
    const measure = () => {
      height = document.documentElement.scrollHeight - window.innerHeight;
    };
    const update = () => {
      ticking = false;
      setProgress(height > 0 ? Math.min(1, window.scrollY / height) : 0);
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measure, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", measure);
    };
  }, []);


  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );
    items.forEach((it) => {
      const el = document.getElementById(it.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [items]);

  return (
    <nav aria-label="On this page" className="hidden lg:block sticky top-24">
      <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
        On this page
      </div>
      <div className="mt-3 h-1 w-full rounded-full bg-surface-2 overflow-hidden">
        <div
          className="h-full bg-primary transition-[width] duration-150"
          style={{ width: `${progress * 100}%` }}
        />
      </div>
      <nav className="mt-5 flex flex-col gap-1 text-sm">
        {items.map((it) => {
          const isActive = active === it.id;
          return (
            <a
              key={it.id}
              href={`#${it.id}`}
              className={`border-l-2 pl-3 py-1.5 transition-colors ${
                isActive
                  ? "border-primary text-foreground"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {it.label}
            </a>
          );
        })}
      </nav>
    </nav>
  );
}
